import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Target, Users, Activity, FileText, Stethoscope, ArrowRight } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function About() {
  return <div className="w-full bg-dark" data-id="element-50">
      {/* Hero / Intro Section */}
      <section className="bg-dark text-white py-24 border-b-4 border-accent relative overflow-hidden" data-id="element-51">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-darkSlate via-dark to-dark opacity-80" data-id="element-52"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-53">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="max-w-3xl" data-id="element-54">
            <h1 className="text-5xl md:text-6xl font-heading font-bold uppercase tracking-tight mb-8" data-id="element-55">
              About <span className="text-accent" data-id="element-56">[CLINIC NAME]</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed mb-12" data-id="element-57">
              We provide practical, performance-driven TRT through secure
              telehealth for men in [STATE] and [CITY]. Our mission is to strip
              away the friction of traditional healthcare and deliver optimized
              results directly to you.
            </p>

            <div className="flex items-center bg-darkSlate p-6 rounded-md border border-darkCard inline-flex shadow-lg" data-id="element-58">
              {/* Geometric Avatar Placeholder */}
              <div className="w-16 h-16 bg-dark rounded-full overflow-hidden mr-6 border-2 border-accent flex-shrink-0 flex items-center justify-center relative shadow-[0_0_15px_rgba(255,107,53,0.2)]" data-id="element-59">
                <div className="absolute inset-0 bg-gradient-to-tr from-accent/20 to-transparent" data-id="element-60"></div>
                <span className="font-heading font-bold text-accent text-xl relative z-10" data-id="element-61">
                  CMO
                </span>
              </div>
              <div data-id="element-62">
                <h3 className="text-lg font-heading font-bold uppercase tracking-wider text-white" data-id="element-63">
                  Clinical Leadership
                </h3>
                <p className="text-accent font-medium mt-1" data-id="element-64">
                  [PROVIDER NAME], MD/DO/NP
                </p>
                <p className="text-sm text-slate-500 mt-1 uppercase tracking-wider" data-id="element-65">
                  Chief Medical Officer
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-24 bg-darkSlate border-b border-darkCard" data-id="element-66">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-67">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mb-16" data-id="element-68">
            <h2 className="text-4xl font-heading font-bold text-white uppercase tracking-tight mb-6" data-id="element-69">
              Our Approach
            </h2>
            <div className="w-24 h-1.5 bg-accent" data-id="element-70"></div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-71">
            {[{
            icon: <Target className="w-8 h-8 text-accent" data-id="element-72" />,
            title: 'Personalized Dosing',
            desc: "We don't treat numbers; we treat symptoms. Your protocol is dialed in specifically for your body's response, ensuring maximum benefit with minimal side effects."
          }, {
            icon: <Activity className="w-8 h-8 text-accent" data-id="element-73" />,
            title: 'Lab-Guided Care',
            desc: 'Every decision is backed by comprehensive bloodwork. We monitor your key biomarkers consistently to keep you in the optimal, healthy range.'
          }, {
            icon: <Shield className="w-8 h-8 text-accent" data-id="element-74" />,
            title: 'Telehealth Convenience',
            desc: 'Expert medical care delivered on your terms. Consultations, follow-ups, and medication delivery are all handled securely without you ever stepping into a waiting room.'
          }].map((item, i) => <motion.div variants={fadeInUp} key={i} className="bg-dark p-8 rounded-md border border-darkCard shadow-lg" data-id="element-75">
                <div className="w-16 h-16 bg-accent/10 flex items-center justify-center rounded-md mb-8 border border-accent/20" data-id="element-76">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-heading font-bold text-white mb-4 uppercase tracking-wide" data-id="element-77">
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-78">{item.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Who We Help Section */}
      <section className="py-24 bg-dark border-b border-darkCard" data-id="element-79">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-80">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-81">
            <h2 className="text-4xl font-heading font-bold text-white uppercase tracking-tight mb-6" data-id="element-82">
              Who We Help
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-83">
              TRT isn't just for bodybuilders. It's for everyday men who refuse
              to let declining hormones dictate their quality of life.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-84">
            {[{
            title: 'Busy Professionals',
            desc: 'Men who need sustained mental clarity and energy to perform at work.'
          }, {
            title: 'Tired Dads',
            desc: 'Fathers looking to reclaim the energy needed to keep up with their kids and family life.'
          }, {
            title: 'Lifters & Athletes',
            desc: 'Active men noticing slower recovery, lost strength, and stubborn body fat.'
          }, {
            title: 'Men Over 30',
            desc: 'Anyone experiencing the classic signs of low T: fatigue, low drive, and brain fog.'
          }].map((profile, i) => <motion.div variants={fadeInUp} key={i} className="bg-darkSlate p-8 rounded-md shadow-lg border border-darkCard hover:border-accent/50 transition-colors group" data-id="element-85">
                <Users className="w-10 h-10 text-darkCard group-hover:text-accent transition-colors mb-6" data-id="element-86" />
                <h3 className="text-xl font-heading font-bold text-white mb-3 uppercase tracking-wide" data-id="element-87">
                  {profile.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-88">
                  {profile.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Treatments / Services Section */}
      <section className="py-24 bg-darkSlate" data-id="element-89">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-90">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mb-16" data-id="element-91">
            <h2 className="text-4xl font-heading font-bold text-white uppercase tracking-tight mb-6" data-id="element-92">
              Treatments & Services
            </h2>
            <div className="w-24 h-1.5 bg-accent" data-id="element-93"></div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="space-y-6" data-id="element-94">
            {[{
            icon: <Stethoscope data-id="element-95" />,
            title: 'Low T Evaluation',
            desc: 'A comprehensive clinical assessment of your symptoms, medical history, and goals by a licensed provider.'
          }, {
            icon: <FileText data-id="element-96" />,
            title: 'Lab Review & Interpretation',
            desc: 'Detailed analysis of your hormone panel, metabolic markers, and overall health indicators.'
          }, {
            icon: <Activity data-id="element-97" />,
            title: 'Ongoing TRT Management',
            desc: 'Prescription testosterone therapy (when clinically indicated) with regular monitoring and dose adjustments.'
          }, {
            icon: <Shield data-id="element-98" />,
            title: 'Adjunct Therapies',
            desc: 'Additional supportive treatments prescribed as clinically appropriate to manage side effects and optimize overall health.'
          }].map((service, i) => <motion.div variants={fadeInUp} key={i} className="flex flex-col sm:flex-row items-start sm:items-center p-6 sm:p-8 bg-dark rounded-md border border-darkCard group hover:border-accent/30 transition-colors" data-id="element-99">
                <div className="w-14 h-14 bg-darkSlate rounded-md flex items-center justify-center shadow-sm text-accent mb-4 sm:mb-0 sm:mr-8 flex-shrink-0 group-hover:bg-accent group-hover:text-white transition-colors border border-darkCard" data-id="element-100">
                  {service.icon}
                </div>
                <div className="flex-grow" data-id="element-101">
                  <h3 className="text-2xl font-heading font-bold text-white mb-3 uppercase tracking-wide" data-id="element-102">
                    {service.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed" data-id="element-103">
                    {service.desc}
                  </p>
                </div>
                <div className="mt-6 sm:mt-0 sm:ml-8 flex-shrink-0" data-id="element-104">
                  <span className="inline-flex items-center text-sm font-bold text-accent uppercase tracking-wider cursor-not-allowed opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-105">
                    Learn more <ArrowRight className="w-5 h-5 ml-2" data-id="element-106" />
                  </span>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Compliance Note */}
      <section className="py-16 bg-dark border-t border-darkCard" data-id="element-107">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-108">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-darkSlate p-8 rounded-md border border-darkCard shadow-lg flex items-start" data-id="element-109">
            <Shield className="w-8 h-8 text-darkCard mr-6 flex-shrink-0 mt-1" data-id="element-110" />
            <div data-id="element-111">
              <h4 className="text-sm font-heading font-bold text-white uppercase tracking-widest mb-3" data-id="element-112">
                Medical Compliance Notice
              </h4>
              <p className="text-sm text-slate-400 leading-relaxed" data-id="element-113">
                All treatments are prescribed only when medically appropriate
                after a thorough evaluation by a licensed medical provider.
                Services are limited to patients located in [STATE]. [STATE
                LICENSE TEXT].
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}